import { useEffect, useReducer, useRef, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import {
  MAX_REVIEW_NOTE_CANDIDATE_LENGTH,
  MAX_REVIEW_NOTE_CANDIDATES,
  MAX_REVIEW_NOTE_TEXT_LENGTH,
  type ReviewSubjectiveEvaluation,
} from "@chessla/shared";
import { saveReviewPlyNote } from "./critical-moments.api";
import { fetchReviewPlyNoteAbortable } from "./review-note-api";
import { ApiRequestError } from "@/shared/api/client";
import { useNotification } from "@/app/context/NotificationContext";
import {
  initialReviewNoteState,
  isReviewDraftInvalid,
  reduceReviewNoteState,
  reviewCandidateError,
  reviewDraftFromNote,
  ReviewNoteLoadGuard,
  reviewNoteTarget,
  reviewTextError,
  reviewTextLength,
  type ReviewDraft,
} from "./review-note-state";

export { isReviewDraftInvalid, reviewNoteTarget, type ReviewDraft } from "./review-note-state";

interface ReviewNoteEditorProps {
  gameId: string;
  ply: number;
  includeReviewIdea: boolean;
}

const statusCopy = {
  loading: "Loading private note…",
  unsaved: "Unsaved changes.",
  saving: "Saving private note…",
  saved: "Saved.",
  "load-error": "Private note could not be loaded. Retry before editing.",
  "save-error": "Private note could not be saved. Your draft is still here.",
} as const;

export const ReviewNoteEditor = ({ gameId, ply, includeReviewIdea }: ReviewNoteEditorProps) => {
  const { showNotification } = useNotification();
  const key = reviewNoteTarget(gameId, ply);
  const [state, dispatch] = useReducer(reduceReviewNoteState, key, initialReviewNoteState);
  const [loadRevision, setLoadRevision] = useState(0);
  const loadGuard = useRef(new ReviewNoteLoadGuard());

  useEffect(() => {
    const guard = loadGuard.current;
    const token = guard.begin(key);
    dispatch({ type: "target", target: key, generation: token.generation });
    void fetchReviewPlyNoteAbortable(gameId, ply, token.signal)
      .then(({ note }) => {
        if (!guard.isCurrent(token)) return;
        dispatch({ type: "loaded", target: key, generation: token.generation, draft: reviewDraftFromNote(note) });
      })
      .catch((error: unknown) => {
        if ((error as { name?: string }).name === "AbortError" || !guard.isCurrent(token)) return;
        dispatch({ type: "load-failed", target: key, generation: token.generation });
      });
    return () => guard.cancel(token);
  }, [gameId, key, loadRevision, ply]);

  const edit = <K extends keyof ReviewDraft>(field: K, value: ReviewDraft[K]) => {
    dispatch({ type: "edit", key: field, value });
  };
  const invalid = isReviewDraftInvalid(state.draft);
  const locked = state.phase === "loading" || state.phase === "saving" || state.phase === "load-error";

  const save = async () => {
    if (locked || invalid) return;
    const target = state.target;
    const generation = state.generation;
    const draft = state.draft;
    dispatch({ type: "save-started" });
    try {
      const { note } = await saveReviewPlyNote(gameId, ply, draft);
      dispatch({
        type: "saved",
        target,
        generation,
        draft: reviewDraftFromNote(note),
        deleted: note === null,
      });
    } catch (error) {
      dispatch({ type: "save-failed", target, generation });
      showNotification(
        error instanceof ApiRequestError ? error.message : "Private note could not be saved. Your draft is still here.",
        "error",
      );
    }
  };

  const textField = (
    field: "intention" | "calculatedLine" | "expectedReply" | "reviewIdea",
    label: string,
    rows = 1,
  ) => {
    const error = reviewTextError(label, state.draft[field]);
    return (
      <TextField
        disabled={locked}
        error={Boolean(error)}
        helperText={error || `${reviewTextLength(state.draft[field])} / ${MAX_REVIEW_NOTE_TEXT_LENGTH}`}
        inputProps={{ "aria-invalid": Boolean(error) }}
        label={label}
        minRows={rows}
        multiline
        onChange={(event) => edit(field, event.target.value)}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
        value={state.draft[field]}
      />
    );
  };

  const candidateError = reviewCandidateError(state.draft.candidateMoves);
  const status = state.message || statusCopy[state.phase];
  const errorState = state.phase === "load-error" || state.phase === "save-error";

  return (
    <Box
      component="section"
      aria-label={`Private note for ply ${ply}`}
      sx={{ display: "grid", gap: 1.25, p: 1.5, bgcolor: "rgba(240,236,226,.04)" }}
    >
      <Box>
        <Typography component="h3" sx={{ fontWeight: 900, textTransform: "uppercase" }}>
          What were you thinking?
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>
          Capture your decision before checking the answer.
        </Typography>
      </Box>

      {textField("intention", "Intention", 2)}
      <TextField
        disabled={locked}
        error={Boolean(candidateError)}
        helperText={
          candidateError ||
          `${state.draft.candidateMoves.length} / ${MAX_REVIEW_NOTE_CANDIDATES} moves · ${MAX_REVIEW_NOTE_CANDIDATE_LENGTH} characters each`
        }
        inputProps={{ "aria-invalid": Boolean(candidateError) }}
        label="Candidate moves"
        onChange={(event) =>
          edit(
            "candidateMoves",
            event.target.value
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
          )
        }
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
        value={state.draft.candidateMoves.join(", ")}
      />

      <Box
        component="details"
        data-tour="review-calculation-details"
        sx={{
          borderBlock: "1px solid",
          borderColor: "divider",
          py: 0.5,
          "& > summary": { cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", fontWeight: 800 },
        }}
      >
        <Box component="summary">Calculation details</Box>
        <Box sx={{ display: "grid", gap: 1.25, pb: 1 }}>
          {textField("calculatedLine", "Calculated line")}
          {textField("expectedReply", "Expected reply")}
          <TextField
            disabled={locked}
            label="How did you evaluate the position?"
            onChange={(event) =>
              edit("subjectiveEvaluation", (event.target.value || null) as ReviewSubjectiveEvaluation | null)
            }
            select
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            value={state.draft.subjectiveEvaluation ?? ""}
          >
            <MenuItem value="">Not recorded</MenuItem>
            <MenuItem value="better">Better</MenuItem>
            <MenuItem value="equal">Equal</MenuItem>
            <MenuItem value="worse">Worse</MenuItem>
            <MenuItem value="unclear">Unclear</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                disabled={locked}
                checked={state.draft.outOfBook}
                onChange={(_, checked) => edit("outOfBook", checked)}
              />
            }
            label="I was out of book"
          />
        </Box>
      </Box>

      {includeReviewIdea && (
        <Box sx={{ display: "grid", gap: 0.5 }}>
          {textField("reviewIdea", "Found during review", 2)}
          <Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>
            Discovered after the game—not part of your original thought.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          py: 1,
          bgcolor: "background.paper",
        }}
      >
        <Button
          disabled={locked || invalid}
          onClick={() => void save()}
          variant="contained"
          sx={{ minHeight: 44, borderRadius: 0, width: { xs: "100%", sm: "auto" } }}
        >
          {state.phase === "saving" ? "Saving…" : "Save private note"}
        </Button>
        {state.phase === "load-error" && (
          <Button onClick={() => setLoadRevision((revision) => revision + 1)} sx={{ minHeight: 44, borderRadius: 0 }}>
            Retry loading
          </Button>
        )}
        {state.phase === "save-error" && (
          <Button onClick={() => void save()} sx={{ minHeight: 44, borderRadius: 0 }}>
            Retry saving
          </Button>
        )}
        <Typography
          role={errorState ? "alert" : "status"}
          aria-live="polite"
          color={errorState ? "error.main" : "text.secondary"}
          sx={{ fontSize: ".75rem" }}
        >
          {status}
        </Typography>
      </Box>
    </Box>
  );
};
